import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import {
  fetchLeaderboardCourses,
  fetchLmsLeaderboard,
  formatLeaderboardValue,
} from '../../services/lmsLeaderboardApi';

const POPUP_FLAG = 'show_lms_leaderboard_popup';
const RANK_MEDALS = ['🥇', '🥈', '🥉'];

export function markLeaderboardPopupForLogin() {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(POPUP_FLAG, '1');
  }
}

export function shouldShowLeaderboardPopup() {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(POPUP_FLAG) === '1';
}

export function clearLeaderboardPopupFlag() {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(POPUP_FLAG);
  }
}

export default function LmsLeaderboardLoginPopup({ open, onClose, currentPhone }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [topThree, setTopThree] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [courseName, setCourseName] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const coursesRes = await fetchLeaderboardCourses();
        const courses = coursesRes.courses || [];
        if (courses.length === 0) {
          if (!cancelled) {
            setTopThree([]);
            setLoading(false);
          }
          return;
        }

        const courseCode = courses[0].courseCode;
        const res = await fetchLmsLeaderboard(courseCode, {
          category: 'overall',
          period: 'week',
        });

        if (!cancelled && res.success) {
          setTopThree((res.topThree || res.entries || []).slice(0, 3));
          setMyRank(res.studentRank || null);
          setCourseName(res.courseName || courseCode);
        }
      } catch (err) {
        console.error('Leaderboard popup load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    clearLeaderboardPopupFlag();
    onClose?.();
  };

  const handleViewFull = () => {
    handleClose();
    navigate('/student/leaderboard');
  };

  const isMe = (phone) => phone === currentPhone;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' },
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)',
          color: 'white',
          px: 3,
          py: 2.5,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EmojiEventsIcon sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Course Leaderboard
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              See how others are performing — set your goal and compete!
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : topThree.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" gutterBottom>
              Be the first to climb the leaderboard!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start videos, quizzes, or Speed Games to appear on the board.
            </Typography>
          </Box>
        ) : (
          <>
            {courseName && (
              <Chip
                label={courseName}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              This week&apos;s top performers
            </Typography>
            {topThree.map((entry, index) => {
              const formatted = formatLeaderboardValue(
                'overall',
                entry.primaryValue,
                entry.secondaryValue,
              );
              return (
                <Box
                  key={entry.studentId || entry.phone || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    bgcolor: index === 0 ? '#FFFBEB' : 'grey.50',
                    border: index === 0 ? '2px solid #FCD34D' : '1px solid #e5e7eb',
                  }}
                >
                  <Typography sx={{ fontSize: 24, minWidth: 32 }}>
                    {RANK_MEDALS[index]}
                  </Typography>
                  <Avatar sx={{ bgcolor: '#2D6A4F' }}>
                    {entry.studentName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight="bold">
                      {entry.studentName}
                      {isMe(entry.phone) && ' (You)'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatted.primary} · {formatted.secondary}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {myRank?.rank > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                color: 'white',
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Your rank this week
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                #{myRank.rank} of {myRank.totalStudents}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {formatLeaderboardValue('overall', myRank.primaryValue, myRank.secondaryValue).primary}
              </Typography>
            </Box>
          </>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic' }}
        >
          Tip: Watch others&apos; progress and set your daily WPM and study goals!
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button fullWidth variant="outlined" onClick={handleClose}>
            Later
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<TrendingUpIcon />}
            onClick={handleViewFull}
            sx={{
              bgcolor: '#2D6A4F',
              '&:hover': { bgcolor: '#1B4332' },
            }}
          >
            View Full Leaderboard
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
